import { type Parsed } from "~/components/canvas/easel/runtime";
import { type FunctionBodyCell } from "~/utils/function-body-cell";

import {
	type BlockStatement,
	type Expression,
	type Identifier,
	type ImportDeclaration,
	type ViewExpression,
	isIdentifier,
	isViewExpression,
} from "./ast";

//
// Cell.
//

export type Cell = AnonymousCell | EmptyCell | NamedCell | ViewOfCell | ViewOfValCell;

export const isCell = (o: Parsed): o is Cell => {
	return Boolean(o && o.type === "Cell");
};

interface CellBase {
	type: "Cell";
	start: number;
	end: number;
	id: Identifier | ViewExpression | null;
	async: boolean;
	generator: boolean;
	body: Expression | BlockStatement | ImportDeclaration | ViewExpression | null;
	references: Identifier[] | null;
}

export interface AnonymousCell extends CellBase {
	type: "Cell";
	id: null;
	body: Expression | BlockStatement | ImportDeclaration;
	references: Identifier[];
}

export const isAnonymousCell = (o: Cell | FunctionBodyCell): o is AnonymousCell => {
	return o && o.type === "Cell" && o.id === null && o.body !== null;
};

export interface EmptyCell extends CellBase {
	type: "Cell";
	id: null;
	async: false;
	generator: false;
	body: null;
	references: null;
}

export const isEmptyCell = (o: Cell): o is EmptyCell => {
	return (
		o &&
		o.type === "Cell" &&
		o.id === null &&
		o.async === false &&
		o.generator === false &&
		o.body === null
	);
};

export interface NamedCell extends CellBase {
	type: "Cell";
	id: Identifier;
	body: Expression | BlockStatement;
	references: Identifier[];
}

export const isNamedCell = (o: Cell | FunctionBodyCell): o is NamedCell => {
	return o && o.type === "Cell" && o.id !== null && isIdentifier(o.id);
};

export interface ViewOfCell extends CellBase {
	type: "Cell";
	id: ViewExpression;
	async: false;
	generator: false;
	body: Expression | BlockStatement;
	references: Identifier[];
}

export const isViewOfCell = (o: Cell | FunctionBodyCell): o is ViewOfCell => {
	return o && o.type === "Cell" && o.id !== null && isViewExpression(o.id);
};

export interface ViewOfValCell extends CellBase {
	type: "Cell";
	id: null;
	body: ViewExpression;
	references: Identifier[];
}
